/**
 * Layout Clone Templates API (SoW-311)
 * API functions for managing component layout presets
 *
 * NOTE: The backend (layoutCloneTemplates.routes.ts) returns BARE bodies with no
 * { success, data } envelope — `res.json(templates)` / `res.json(template)`. The fetch
 * client surfaces that body directly, so we cast and read it as-is. Using unwrapData here
 * read a non-existent `.data` property and always yielded []/undefined.
 */

import { api } from './client'
import type {
  LayoutCloneTemplate,
  CreateLayoutCloneTemplateDTO,
  UpdateLayoutCloneTemplateDTO,
} from '@fmeastudio/shared'

/**
 * Get all layout templates for a project
 */
export async function getLayoutTemplates(projectId: string): Promise<LayoutCloneTemplate[]> {
  // BE (layoutCloneTemplates.routes.ts:30) returns the bare array.
  const response = (await api.get(
    `/v1/projects/${projectId}/layout-templates`
  )) as unknown as LayoutCloneTemplate[]
  return Array.isArray(response) ? response : []
}

/**
 * Get a single layout template
 */
export async function getLayoutTemplate(
  projectId: string,
  templateId: string
): Promise<LayoutCloneTemplate> {
  // BE (layoutCloneTemplates.routes.ts:56) returns the bare template object.
  return (await api.get(
    `/v1/projects/${projectId}/layout-templates/${templateId}`
  )) as unknown as LayoutCloneTemplate
}

/**
 * Create a new layout template
 */
export async function createLayoutTemplate(
  projectId: string,
  dto: CreateLayoutCloneTemplateDTO
): Promise<LayoutCloneTemplate> {
  // BE (layoutCloneTemplates.routes.ts:86) returns the bare created template.
  return (await api.post(
    `/v1/projects/${projectId}/layout-templates`,
    dto
  )) as unknown as LayoutCloneTemplate
}

/**
 * Update an existing layout template
 */
export async function updateLayoutTemplate(
  projectId: string,
  templateId: string,
  dto: UpdateLayoutCloneTemplateDTO
): Promise<LayoutCloneTemplate> {
  // BE (layoutCloneTemplates.routes.ts:120) returns the bare updated template.
  return (await api.patch(
    `/v1/projects/${projectId}/layout-templates/${templateId}`,
    dto
  )) as unknown as LayoutCloneTemplate
}

/**
 * Delete a layout template
 */
export async function deleteLayoutTemplate(
  projectId: string,
  templateId: string
): Promise<void> {
  await api.delete(`/v1/projects/${projectId}/layout-templates/${templateId}`)
}
